<template>
  <div class="ReviewCard" :style="cardStyle">
    <StarRating :rating="rating" class="ReviewCard__rating" />

    <p v-if="$slots.text" class="ReviewCard__text">
      <slot name="text" />
    </p>

    <div class="ReviewCard__company">
      <div
        v-if="$slots['company-logo'] || resolvedLogoSrc"
        class="ReviewCard__company-logo"
      >
        <slot v-if="$slots['company-logo']" name="company-logo" />
        <img
          v-else
          :src="resolvedLogoSrc"
          :alt="resolvedLogoAlt"
          loading="lazy"
        />
      </div>

      <div class="ReviewCard__employee-info">
        <div class="ReviewCard__employee-name">{{ name }}</div>
        <div class="ReviewCard__employee-job-title">{{ jobTitle }}</div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import StarRating from '@/views/components/ui/rating/UIStarRating.vue'
import {
  REVIEW_LOGOS,
  type ReviewLogoKey,
} from '@/views/components/cards/assets/reviewLogos'

defineSlots<{
  text?: () => any
  'company-logo'?: () => any
}>()

const props = withDefaults(
  defineProps<{
    rating: number
    name: string
    jobTitle: string
    /** Optional logo image src when not using the slot */
    companyLogoSrc?: string
    /** Optional alt text for logo; defaults to name */
    companyLogoAlt?: string
    /** Optional semantic key to choose a known company logo */
    companyLogoKey?: ReviewLogoKey
    /** Custom height for the company logo; accepts px string or number */
    logoHeight?: string | number
  }>(),
  {
    companyLogoSrc: '',
    companyLogoAlt: '',
    logoHeight: '32px',
  },
)

const resolvedLogoSrc = computed(() => {
  if (props.companyLogoSrc) return props.companyLogoSrc
  if (props.companyLogoKey) return REVIEW_LOGOS[props.companyLogoKey].src
  return ''
})

const resolvedLogoAlt = computed(() => {
  if (props.companyLogoAlt) return props.companyLogoAlt
  if (props.companyLogoKey) return REVIEW_LOGOS[props.companyLogoKey].alt
  return props.name
})

const cardStyle = computed(() => {
  const height =
    typeof props.logoHeight === 'number'
      ? `${props.logoHeight}px`
      : props.logoHeight

  return {
    '--logo-height': height,
  }
})
</script>

<style lang="scss" scoped>
.ReviewCard {
  --logo-height: 32px;
  background-color: $color-ui-gray-cool-50;
  padding: 1rem;
  border-radius: 0.75rem;
  display: flex;
  flex-direction: column;
  max-width: 50rem;
  height: 100%;

  @include md {
    padding: 2rem;
  }

  .ReviewCard__rating {
    margin-bottom: 1rem;
  }

  .ReviewCard__text {
    @include font-size-18px();
    font-weight: 500;
    color: $color-ui-gray-cool-900;
    margin-bottom: 1.5rem;
  }

  .ReviewCard__company {
    margin-top: auto;
    display: flex;
    align-items: center;
    gap: 1.5rem;
    margin-top: auto;
  }

  .ReviewCard__company-logo {
    display: flex;
    align-items: center;

    img {
      height: var(--logo-height);
      width: auto;
      display: block;
    }
  }

  .ReviewCard__employee-info {
    @include font-size-16px();
  }

  .ReviewCard__employee-name {
    font-weight: 600;
    color: $color-ui-gray-cool-900;
  }

  .ReviewCard__employee-job-title {
    color: $color-ui-gray-cool-600;
  }
}
</style>
